import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  LoadingController,
  ModalController,
} from "ionic-angular";
import { ComplianceRules, RequirementsRequest } from "models/payers/payer";
import moment from "moment";
import { Config } from "pages/patient-chart/patient-benefits/models";
import { from } from "rxjs/observable/from";
import { of } from "rxjs/observable/of";
import { catchError, map, mergeMap, toArray } from "rxjs/operators";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";
import { ContractPeriod } from "../rates-payer/models";

@Component({
  selector: "requirements",
  templateUrl: "requirements.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Requirements),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => Requirements),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class Requirements implements OnInit {
  @Input() currentPayer: any;
  @Output() onPayerRequirementsSaved = new EventEmitter<any>();
  @Output() onSetCheckmark = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();
  @Input() currentBenefitType: any;

  //requirements: FormGroup;
  saveEnabled: true;
  hasHospice: boolean = false;
  hasPalliative: boolean = false;
  hasRNB: boolean = false;
  selectedEffectiveDate: any;
  tenantId: any;
  payerId: any;
  payerType: any;
  selectedIndex = 0;
  data: any;
  errors: boolean = false;
  notices: any;
  recertification: any;
  faceToFace: any;
  visits: any;
  calculateSia: any;
  sequestrationEnabled: any;
  medications: any;
  useQCodesAsHCPCSForLOC: any;
  includeDayOfDeath: any;
  includeDayOfDischarge: any;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  contractPeriodsOptions: ContractPeriod[];
  currentContractPeriod: any = null;
  previousContractPeriod: any;
  showSection: boolean = false;
  selectedBenefitType: any;

  payerTypeList: Config[] = [
    { name: "Commercial", value: "COMMERCIAL", disabled: false },
    { name: "Medicaid", value: "MEDICAID", disabled: false },
    { name: "Medicare", value: "MEDICARE", disabled: false },
  ];

  constructor(
    private payersService: PayersService,
    private utilityService: UtilityService,
    protected alertController: AlertController,
    public modalCtrl: ModalController,
    private loadingCtrl: LoadingController
  ) {}

  get isMedicare(): boolean {
    return (
      this.currentPayer &&
      this.currentPayer.payerType &&
      this.currentPayer.payerType.toString().toUpperCase() === "MEDICARE"
    );
  }

  // CO-3334 (epic CO-1390): the Medicare hospice Requirements tab is
  // read-only/non-editable — the CO-3157 fixed-value view — for every
  // tenant. An earlier iteration made it editable when the
  // medicare_hospice_visit_billing flag was ON; product reversed that
  // decision (Claudia Baker, 2026-08-10): Requirements stays read-only and
  // per-discipline visit pricing is configured on the Rates > Hospice
  // Visits tab instead. That rates tab remains flag-gated in rates-payer.
  get isMedicareReadOnly(): boolean {
    return this.isMedicare;
  }

  ngOnInit(): void {
    if (this.isMedicareReadOnly) {
      return;
    }
    this.initializeEditableTab();
    this.validateCheckmarkInfo().then((result) => {
      if (result) this.onPayerRequirementsSaved.emit();
    });
  }

  private initializeEditableTab(): void {
    this.contractPeriodsOptions = [];
    if (
      this.currentPayer &&
      this.currentPayer.payerId &&
      this.currentPayer.payerId.length > 0 &&
      this.currentPayer.id != 0
    ) {
      this.tenantId = this.currentPayer.tenantId;
      this.payerId = this.currentPayer.payerId;
      this.payerType = this.currentPayer.payerType;
      this.getBenefitType();
      this.getPayerBillingOptionsPeriods();
    } else {
      this.loadOptions();
    }
  }

  onPeriodChanged() {
    this.errors = true;
    this.showSection = false;
    this.currentContractPeriod = null;
    this.clearContractPeriodBillingOptions();
    this.getPayerBillingOptionsPeriods();
  }

  ngOnChanges() {
    if (this.isMedicareReadOnly) {
      return;
    }
    this.initializeEditableTab();
  }

  private validateCheckmarkInfo() {
    this.getBenefitType();

    return new Promise((resolve, reject) => {
      const apiCalls = [];
      if (this.selectedBenefitType === "HOSPICE") {
        apiCalls.push(
          this.payersService.getPayerBillingOptionsPeriods(
            this.currentPayer.payerId,
            "HOSPICE",
            "REQUIREMENTS"
          )
        );
      } else if (this.selectedBenefitType === "ROOMANDBOARD") {
        apiCalls.push(
          this.payersService.getPayerBillingOptionsPeriods(
            this.currentPayer.payerId,
            "ROOMANDBOARD",
            "REQUIREMENTS"
          )
        );
      }
      from(apiCalls)
        .pipe(
          mergeMap((apiCall, index) => {
            return apiCall.pipe(
              catchError((error) => {
                return of(null);
              })
            );
          }),
          toArray(),
          map((results) => {
            // @ts-ignore
            const hasValidResult = results.some(
              (result) =>
                // @ts-ignore
                result !== null && result.length > 0
            );

            resolve(hasValidResult);
            return hasValidResult;
          })
        )
        .subscribe({
          error: (error) => reject(error),
          complete: () => {},
        });
    });
  }

  getBenefitType() {
    if (
      this.currentPayer &&
      this.currentBenefitType &&
      this.currentBenefitType.toUpperCase().includes("HOSPICE")
    ) {
      this.selectedBenefitType = "HOSPICE";
    } else if (
      this.currentPayer &&
      this.currentBenefitType &&
      this.currentBenefitType
        .toUpperCase()
        .replaceAll(" ", "")
        .includes("ROOMANDBOARD")
    ) {
      this.selectedBenefitType = "ROOMANDBOARD";
    }
  }

  changeBenefitType(event: any, index?: number) {
    if (index !== undefined) {
      this.selectedIndex = index;
    }
    this.selectedBenefitType = event;
    this.showSection = false;
    this.errors = true;
    this.currentContractPeriod = null;
    this.getPayerBillingOptionsPeriods();
  }

  checkTabEnabled(benefitType: string) {
    return (
      this.currentPayer &&
      this.currentPayer.benefitType &&
      this.currentPayer.benefitType
        .replace(/ /g, "")
        .toUpperCase()
        .indexOf(benefitType) >= 0
    );
  }

  initForm(effectiveDate) {
    if (this.currentPayer) {
      let data = { ...this.currentPayer.complianceRules };

      if (
        this.currentPayer &&
        (this.currentPayer.payerType.toLowerCase() == "medicaid" ||
          this.currentPayer.payerType.toLowerCase() == "commercial") &&
        this.currentPayer.complianceRules &&
        !this.currentPayer.complianceRules.updated
      ) {
        data.noeRequired = false;
        data.includeVisits = true;
        data.includeMedications = true;
      }

      if (this.isMedicare && data && !data.updated) {
        this.seedMedicareDefaults(data);
      }

      this.notices = data.noeRequired ? "yes" : "no";
      this.recertification = data.recertificationRequired ? "yes" : "no";
      this.faceToFace = data.faceToFaceRequired ? "yes" : "no";
      this.visits = data.includeVisits ? "yes" : "no";
      this.calculateSia = data.calculateSia ? "yes" : "no";
      this.sequestrationEnabled = data.sequestrationEnabled ? "yes" : "no";
      this.medications = data.includeMedications ? "yes" : "no";
      this.useQCodesAsHCPCSForLOC = data.useQCodesAsHCPCSForLOC ? "yes" : "no";
      this.includeDayOfDeath =
        data.includeDayOfDeath === undefined
          ? this.selectedBenefitType === "HOSPICE"
            ? "yes"
            : "no"
          : data.includeDayOfDeath
          ? "yes"
          : "no";
      this.includeDayOfDischarge =
        data.includeDayOfDischarge === undefined
          ? this.selectedBenefitType === "HOSPICE"
            ? "yes"
            : "no"
          : data.includeDayOfDischarge
          ? "yes"
          : "no";
    }

    if (effectiveDate) {
      this.getPayerRequirements(this.selectedBenefitType, effectiveDate);
    }
  }

  setFormData() {
    let complianceData: ComplianceRules = {
      faceToFaceBenefitPeriod: 0,
      faceToFaceRequired: false,
      noeRequired: false,
      recertificationInterval: "",
      recertificationRequired: false,
      updated: false,
      includeVisits: false,
      calculateSia: false,
      sequestrationEnabled: false,
      includeMedications: false,
      useQCodesAsHCPCSForLOC: false,
      includeDayOfDeath: false,
      includeDayOfDischarge: false,
    };

    if (this.currentPayer && this.currentPayer.complianceRules) {
      complianceData = { ...this.currentPayer.complianceRules };
    }

    if (
      this.currentPayer &&
      (this.currentPayer.payerType.toLowerCase() == "medicaid" ||
        this.currentPayer.payerType.toLowerCase() == "commercial") &&
      this.currentPayer.complianceRules &&
      !this.currentPayer.complianceRules.updated
    ) {
      complianceData.noeRequired = false;
      complianceData.includeVisits = true;
      complianceData.includeMedications = true;
    }

    if (this.isMedicare && !complianceData.updated) {
      this.seedMedicareDefaults(complianceData);
    }

    this.notices = complianceData.noeRequired ? "yes" : "no";
    this.recertification = complianceData.recertificationRequired
      ? "yes"
      : "no";
    this.faceToFace = complianceData.faceToFaceRequired ? "yes" : "no";
    this.visits = complianceData.includeVisits ? "yes" : "no";
    this.calculateSia = complianceData.calculateSia ? "yes" : "no";
    this.sequestrationEnabled = complianceData.sequestrationEnabled
      ? "yes"
      : "no";
    this.medications = complianceData.includeMedications ? "yes" : "no";
    this.useQCodesAsHCPCSForLOC = complianceData.useQCodesAsHCPCSForLOC
      ? "yes"
      : "no";
    this.includeDayOfDeath =
      complianceData.includeDayOfDeath === undefined
        ? this.selectedBenefitType === "HOSPICE"
          ? "yes"
          : "no"
        : complianceData.includeDayOfDeath
        ? "yes"
        : "no";
    this.includeDayOfDischarge =
      complianceData.includeDayOfDischarge === undefined
        ? this.selectedBenefitType === "HOSPICE"
          ? "yes"
          : "no"
        : complianceData.includeDayOfDischarge
        ? "yes"
        : "no";
  }

  // CO-3334: seed values for a Medicare payer with no saved Requirements
  // record, mirroring the fixed values the CO-3157 read-only view displays
  // (statutory NOE/recert/face-to-face + visits/SIA/medications/
  // sequestration on, Q-codes off) so flipping the flag ON presents the
  // same answers, now editable.
  private seedMedicareDefaults(data: any): void {
    data.noeRequired = true;
    data.recertificationRequired = true;
    data.faceToFaceRequired = true;
    data.includeVisits = true;
    data.calculateSia = true;
    data.includeMedications = true;
    data.sequestrationEnabled = true;
    data.useQCodesAsHCPCSForLOC = false;
    data.includeDayOfDeath = true;
    data.includeDayOfDischarge = true;
  }

  getPayerRequirements(benefitType, effectiveDate) {
    let spinnerText = "Loading Requirements Data";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.payersService
      .getPayerRequirements(
        this.currentPayer.payerId,
        benefitType,
        effectiveDate
      )
      .subscribe((data) => {
        if (data != null) {
          let requirements = Object.assign({}, data);
          this.notices = requirements.noeRequired ? "yes" : "no";
          this.recertification = requirements.recertificationRequired
            ? "yes"
            : "no";
          this.faceToFace = requirements.faceToFaceRequired ? "yes" : "no";
          this.visits = requirements.includeVisits ? "yes" : "no";
          this.calculateSia = requirements.calculateSia ? "yes" : "no";
          this.sequestrationEnabled = requirements.sequestrationEnabled
            ? "yes"
            : "no";
          this.medications = requirements.includeMedications ? "yes" : "no";
          this.useQCodesAsHCPCSForLOC = requirements.useQCodesAsHCPCSForLOC
            ? "yes"
            : "no";
          this.includeDayOfDeath =
            requirements.includeDayOfDeath === undefined
              ? this.selectedBenefitType === "HOSPICE"
                ? "yes"
                : "no"
              : requirements.includeDayOfDeath
              ? "yes"
              : "no";
          this.includeDayOfDischarge =
            requirements.includeDayOfDischarge === undefined
              ? this.selectedBenefitType === "HOSPICE"
                ? "yes"
                : "no"
              : requirements.includeDayOfDischarge
              ? "yes"
              : "no";
          this.showSection = true;
          this.errors = false;
          this.onPayerRequirementsSaved.emit();
          spinner.dismiss();
        } else {
          this.showSection = true;
          this.setFormData();
          this.errors = true;
          spinner.dismiss();
        }
      });
  }

  showSavePayerModal() {
    if (
      this.contractPeriodsOptions != null &&
      this.contractPeriodsOptions.length > 1
    ) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveRequirements();
        }
      });
      modalPage.present();
    } else {
      this.saveRequirements();
    }
  }

  public saveRequirements(): void {
    let request = this.createRequest();
    this.utilityService.increaseSpinnerCount("Saving...");
    this.payersService.postPayerRequirements(request).subscribe((data) => {
      setTimeout(() => {
        this.utilityService.decreaseSpinnerCount();
        this.onPayerRequirementsSaved.emit();
        this.errors = true;
        this.showSection = false;
        this.currentContractPeriod = null;
        this.getPayerBillingOptionsPeriods();
        this.showAlert({
          title: "Success",
          message: "Requirements data saved",
        });
      }, 2000);
    }, this.onSaveError);
  }

  public cancel(): void {
    this.onCanceled.emit(true);
  }

  public createRequest(): any {
    let body = this.mapRequirementsForm();
    return body;
  }

  mapRequirementsForm() {
    const r: RequirementsRequest = {} as any as RequirementsRequest;
    r.faceToFaceRequired = this.faceToFace == "yes";
    r.noeRequired = this.notices == "yes";
    r.recertificationRequired = this.recertification == "yes";
    r.includeVisits = this.visits == "yes";
    r.calculateSia = this.calculateSia == "yes" && this.visits == "yes";
    r.sequestrationEnabled = this.sequestrationEnabled == "yes";
    r.includeMedications = this.medications == "yes";
    r.useQCodesAsHCPCSForLOC = this.useQCodesAsHCPCSForLOC == "yes";
    r.includeDayOfDeath = this.includeDayOfDeath == "yes";
    r.includeDayOfDischarge = this.includeDayOfDischarge == "yes";

    r.benefitType = this.selectedBenefitType;
    r.start = moment(new Date(this.getEffectiveDate())).format(this.dateFormat);
    r.payerId = this.currentPayer.payerId;
    if (this.getExpiredDate() != "") {
      r.end = moment(new Date(this.getExpiredDate())).format(this.dateFormat);
    }
    return r;
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving the rates. " + error.message,
        });
      }
    }
  };

  public validateFields() {
    return this.currentPayer != null && this.currentPayer.payerId;
  }

  async onContractPeriodChange(event: any) {
    if (!event) {
      this.currentContractPeriod = this.emptyContractPeriod();
      this.errors = true;
      this.previousContractPeriod = this.currentContractPeriod;
      this.clearContractPeriodBillingOptions();
      this.showSection = false;
      return;
    }
    if (event.value == "Add New") {
      this.addNewContractPeriod();
    } else {
      this.currentContractPeriod = {
        id: event.id,
        name: event.name,
        value: event.value,
        expiredDate: event.expiredDate,
        effectiveDate: event.effectiveDate,
      };

      this.initForm(this.currentContractPeriod.effectiveDate);

      this.previousContractPeriod = this.currentContractPeriod;
      this.showSection = true;
      this.errors = false;
    }
  }

  onContractPeriodClear(event: any) {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.id != 0
    );
    this.loadOptions();
  }

  addNewContractPeriod() {
    let minDate: moment.Moment = moment("2023-09-30", this.dateFormat);
    this.contractPeriodsOptions.forEach((contractPeriod) => {
      let periodStart = moment(contractPeriod.effectiveDate);
      if (periodStart.isAfter(minDate)) {
        minDate = periodStart;
      }
    });

    let contractPeriodModal = this.modalCtrl.create(
      "ContractPeriodModalComponent",
      { minDateFormat: minDate },
      { cssClass: "contract-period-modal" }
    );

    contractPeriodModal.onDidDismiss((data) => {
      if (data) {
        let newOption = this.formatContractPeriodModel(data);
        let contractPeriods = this.contractPeriodsOptions.filter(
          (contract) => contract.value != "Add New"
        );
        if (
          contractPeriods.length === 0 ||
          newOption.effectiveDate >
            this.getMostRecentContractPeriod(contractPeriods).effectiveDate
        ) {
          this.saveNewContractPeriodOption(newOption);
        } else {
          this.showAlert({
            title: "Error",
            message:
              "Contract period’s effective date can’t be before or equal to the active period’s date.",
          });
          this.onNewPeriodCanceled();
        }
      } else {
        this.onNewPeriodCanceled();
      }
    });
    contractPeriodModal.present();
  }

  onNewPeriodCanceled() {
    this.currentContractPeriod =
      this.previousContractPeriod &&
      this.previousContractPeriod.value != "Add New"
        ? this.previousContractPeriod
        : this.emptyContractPeriod();
  }

  saveNewContractPeriodOption(newOption) {
    this.contractPeriodsOptions = [newOption, ...this.contractPeriodsOptions];
    this.currentContractPeriod = newOption;
    this.clearContractPeriodBillingOptions();
    this.removeNewContractPeriodOption();
    this.errors = false;
    this.showSection = true;
  }

  removeNewContractPeriodOption() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.value != "Add New"
    );
  }

  formatContractPeriodModel(period: any): ContractPeriod {
    let newContractPeriod = moment(new Date(period)).format(this.dateUIFormat);
    let newEffectiveDate = moment(new Date(period)).format(this.dateFormat);
    return {
      id: 0,
      name: newContractPeriod,
      value: newContractPeriod,
      effectiveDate: newEffectiveDate,
      expiredDate: null,
    };
  }

  getPayerBillingOptionsPeriods() {
    let spinnerText = "Loading Contract Periods";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.payersService
      .getPayerBillingOptionsPeriods(
        this.currentPayer.payerId,
        this.selectedBenefitType,
        "REQUIREMENTS"
      )
      .pipe()
      .take(1)
      .subscribe((response) => {
        spinner.dismiss();
        let previousContractPeriods: ContractPeriod[] = [];
        if (response !== null) {
          let contractPeriod = Object.values(response);
          let count = 1;
          contractPeriod.forEach((period) => {
            let periodStart = moment(period.start);
            let name = periodStart.format(this.dateUIFormat);
            if (period.end && period.end != "") {
              name =
                name + " - " + moment(period.end).format(this.dateUIFormat);
            }
            let newPeriod = {
              id: count,
              name: name,
              value: periodStart.format(this.dateUIFormat),
              effectiveDate: periodStart.format(this.dateFormat),
              expiredDate: period.end
                ? moment(period.end).format(this.dateFormat)
                : "",
            };

            previousContractPeriods.push(newPeriod);
            count++;
          });
        }
        this.contractPeriodsOptions = previousContractPeriods.sort(
          (a, b) =>
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
        );
        this.loadOptions();
      });
  }

  getMostRecentContractPeriod(contractPeriods) {
    return contractPeriods.sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime()
    )[0];
  }

  clearContractPeriodBillingOptions() {
    this.initForm(null);
  }

  formatToTwoDecimals(num: number): string {
    return num.toFixed(2);
  }

  showError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  getEffectiveDate() {
    return this.currentContractPeriod &&
      this.currentContractPeriod.effectiveDate
      ? this.formatDate(this.currentContractPeriod.effectiveDate)
      : "";
  }

  getExpiredDate() {
    return this.currentContractPeriod && this.currentContractPeriod.expiredDate
      ? this.formatDate(this.currentContractPeriod.expiredDate)
      : "";
  }

  formatDate(dateString: string): string {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    const formattedMonth = (date.getMonth() + 1).toString().padStart(2, "0");
    const formattedDay = date.getDate().toString().padStart(2, "0");
    const formattedYear = date.getFullYear();

    return `${formattedMonth}/${formattedDay}/${formattedYear}`;
  }

  emptyContractPeriod() {
    return null;
  }

  loadOptions(): void {
    let addNewContractPeriodOption: ContractPeriod = {
      id: 0,
      name: "Add New",
      value: "Add New",
      effectiveDate: "",
      expiredDate: "",
    };
    this.contractPeriodsOptions = [
      addNewContractPeriodOption,
      ...this.contractPeriodsOptions,
    ];
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/requirements/requirements.ts